<script setup lang="ts">
import { ref } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import { Loader2 } from 'lucide-vue-next'
import InternalLayout from '@/layouts/InternalLayout.vue'
import { zipCodeLookup } from '@/routes'

const crewNotes = [
  'We make your mow day a priority.',
  'Mow day may vary in instances of crew delay (equipment problems or weather related delays)',
  'Crews determine their own routes, so times may vary depending on work load.',
  'Crew mow all holidays except Thanksgiving, Christmas, and New Years.',
]

const zip = ref('')
const loading = ref(false)
const phoneNumber = ref('')
const errorMessage = ref('')

function csrfToken(): string {
  return decodeURIComponent(document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? '')
}

async function lookupZip(): Promise<void> {
  if (!zip.value.trim()) { return }

  loading.value = true
  phoneNumber.value = ''
  errorMessage.value = ''

  try {
    const response = await fetch(zipCodeLookup.url({ zip: zip.value.trim() }), {
      headers: {
        'Accept': 'application/json',
        'X-XSRF-TOKEN': csrfToken(),
      },
    })

    if (response.ok) {
      const data = await response.json()
      phoneNumber.value = data.phone_number || '817-886-3411'
    } else {
      errorMessage.value = 'Mow Managers does not service that area.'
    }
  } catch {
    errorMessage.value = 'Mow Managers does not service that area.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <InternalLayout title="Contact Us" hero-image="/img/charming-brick-home-green-yard.jpg" hero-position="center 85%">
    <Head title="Contact Us">
      <meta head-key="description" name="description" content="Contact Mow Managers for lawn mowing services. Office hours Monday-Friday 9am-5pm with 24/7 customer service via text." />
      <link rel="canonical" href="https://mowmanagers.com/contact-us" />
      <meta property="og:title" content="Contact Us - Mow Managers" />
      <meta property="og:description" content="Contact Mow Managers for lawn mowing services. Office hours Monday-Friday 9am-5pm with 24/7 customer service via text." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mowmanagers.com/contact-us" />
    </Head>

    <!-- ==================== PHONE LOOKUP ==================== -->
    <section aria-labelledby="contact-heading" class="py-20 lg:py-28 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
              Get In Touch
            </p>
            <h2 id="contact-heading" class="text-mm-dark text-3xl sm:text-4xl lg:text-[70px] font-bold lg:leading-[80px] mt-3">
              Enter Your Zip<br />to Call or Text Us
            </h2>
            <p class="text-[#6d706d] text-[20px] font-medium mt-6 leading-[34px] max-w-[628px]">
              We're committed to providing the best customer service, fast and reliable communication, and a team that answers the phone when you call.
            </p>
            <form class="mt-8 max-w-sm" @submit.prevent="lookupZip">
              <label for="contact-zip" class="text-mm-dark text-[14px] font-bold">Zip Code <span class="text-red-500" aria-hidden="true">*</span></label>
              <input
                id="contact-zip"
                v-model="zip"
                type="text"
                required
                aria-required="true"
                placeholder="Zip Code*"
                class="mt-2 w-full border border-gray-300 rounded-md px-4 py-3 text-[16px] text-gray-900 placeholder-[#717680] focus:outline-none focus:ring-2 focus:ring-mm-green focus:border-transparent"
              />
              <button
                type="submit"
                :disabled="loading"
                class="mt-4 w-full sm:w-auto bg-mm-green text-white font-bold uppercase tracking-[1.6px] px-8 py-3 rounded-md hover:bg-mm-green-dark transition-colors text-[14px] cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Loader2 v-if="loading" class="size-4 animate-spin" />
                Get Phone Number
              </button>

              <!-- Phone number result -->
              <div v-if="phoneNumber" class="mt-6 rounded-lg bg-mm-green/10 border border-mm-green px-5 py-4">
                <p class="text-sm font-bold uppercase tracking-[1.4px] text-mm-dark">Call or Text Us</p>
                <a
                  :href="`tel:${phoneNumber}`"
                  class="mt-1 block text-2xl font-bold text-mm-green hover:underline"
                >
                  {{ phoneNumber }}
                </a>
              </div>

              <!-- Error message -->
              <div v-if="errorMessage" class="mt-6 rounded-lg bg-red-50 border border-red-200 px-5 py-4">
                <p class="text-sm font-semibold text-red-700">{{ errorMessage }}</p>
              </div>
            </form>
          </div>

          <div class="rounded-[20px] overflow-hidden">
            <img
              src="/img/local-lawn-care.webp"
              alt="Mow Managers lawn care professional mowing a yard"
              class="w-full h-full object-cover"
              width="640"
              height="480"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== OFFICE HOURS + FIELD CREW HOURS ==================== -->
    <section aria-labelledby="hours-heading" class="bg-mm-dark py-20 lg:py-28">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 id="hours-heading" class="sr-only">Hours of Operation</h2>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          <!-- OFFICE HOURS -->
          <div>
            <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
              Office Hours
            </p>
            <h3 class="text-white text-[28px] lg:text-[36px] font-bold mt-4 leading-tight">
              Monday – Friday
            </h3>
            <h3 class="text-white text-[28px] lg:text-[36px] font-bold leading-tight">
              <time>9am – 5pm</time>
            </h3>
            <p class="text-white/80 text-[18px] font-medium mt-6 leading-[30px]">
              24/7 Customer Service via Text.
            </p>
            <div class="flex items-start gap-3 mt-6">
              <div class="w-[4px] bg-mm-green self-stretch rounded-full shrink-0" aria-hidden="true" />
              <p class="text-white text-[18px] font-medium leading-[30px]">
                We answer the phone<br />when you call.
              </p>
            </div>
          </div>

          <!-- FIELD CREW HOURS -->
          <div>
            <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
              Field Crew Hours
            </p>
            <h3 class="text-white text-[28px] lg:text-[36px] font-bold mt-4 leading-tight">
              Your Mow Day<br />Is a Priority
            </h3>
            <ul class="mt-6 space-y-4 list-none">
              <li
                v-for="note in crewNotes"
                :key="note"
                class="flex items-start gap-3"
              >
                <div class="w-2 h-2 rounded-full bg-mm-green shrink-0 mt-2.5" aria-hidden="true" />
                <p class="text-white/80 text-[18px] font-medium leading-[30px]">{{ note }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== CTA ==================== -->
    <section aria-labelledby="cta-heading" class="bg-mm-green py-16 lg:py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 id="cta-heading" class="text-white text-3xl sm:text-4xl lg:text-[50px] font-bold lg:leading-[60px]">
          Ready to Get Started?
        </h2>
        <p class="text-white/90 text-[20px] font-medium mt-4 max-w-xl mx-auto">
          Get a free price quote in seconds. Just enter your address and zip code.
        </p>
        <Link
          href="/get-started"
          class="inline-block mt-8 bg-white text-mm-dark font-bold uppercase tracking-[1.6px] px-10 py-3 rounded-md hover:bg-mm-light transition-colors text-[16px] cursor-pointer"
        >
          Free Price Quote
        </Link>
      </div>
    </section>
  </InternalLayout>
</template>
